import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U3120CustomerContact } from "../../../../U3120Service";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import type { U3120CustomerFormExtendedHandlers } from "../../../U3120CustomerFormTypes";

// Gestion des handlers pour les contacts d'un client
export interface U3120CustomerContactHandler {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3120CustomerContact) => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3120CustomerContact>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3120CustomerContact>>;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des contacts d'un client
export interface U3120CustomerContactHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3120CustomerContact>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3120CustomerContact>>;
  parentFormHandlers: U3120CustomerFormExtendedHandlers;
}

// Gestion des handlers pour les contacts d'un client
export interface U3120CustomerContactHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3120CustomerContact) => void;

  onDlgClose: (result: string) => void;

  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;

  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3120CustomerContact>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3120CustomerContact>>;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les contacts d'un client
export function u3120CustomerContactHandler(params: U3120CustomerContactHandlerParam): U3120CustomerContactHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  // Gestion de l'ajout d'un contact
  const onAdd = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
    return;
  };

  // Gestion de la modification d'un contact
  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as U3120CustomerContact);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
    return;
  };

  // Gestion de la suppression d'un contact
  const onDelete = (): void => {
    setDeleteDialogOpen(true);
    return;
  };

  // Gestion du clic sur une ligne pour ouvrir le dialog
  // En mode édition du parent : ouvre en mode édition
  // En mode lecture du parent : ouvre en mode lecture seule
  const onRowClick = (row: U3120CustomerContact): void => {
    formStateHandlers.setData(row);
    const parentFormState = parentFormHandlers.getFormState?.();
    if (parentFormState === U2000FormStateEnumState.EDIT) {
      // Si le formulaire parent est en mode édition, ouvrir le dialog en mode édition
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    } else {
      // Sinon, ouvrir en mode lecture seule
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
    }
    setDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      // Valider les données avant de sauvegarder
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers && businessHandlers.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        // Si des erreurs de validation existent, ne pas fermer le dialogue
        if (Object.keys(errors).length > 0) {
          return; // Ne pas fermer le dialogue si des erreurs de validation existent
        }
      }
      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        // important: mettre à jour la ligne dans le gridStateHandlers
        const updatedData = gridStateHandlers.getData().map((item) => (item.id === formStateHandlers.getData().id ? { ...formStateHandlers.getData() } : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent(dataChangedEvent + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        const newData = formStateHandlers.getData();

        // Générer un UUID temporaire pour les nouveaux enregistrements
        newData.id = generateTemporaryId();
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent(dataChangedEvent + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
    return;
  };

  // Gestion de la fermeture du dialog de suppression
  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);

      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return { onAdd, onModify, onDelete, onRowClick, onDlgClose, isDialogOpen, setDialogOpen, formStateHandlers, gridStateHandlers, isDeleteDialogOpen, onDlgDeleteClose, dataChangedEvent };
}
